import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, ShieldAlert, Copy, Share2, Printer,
  QrCode, ExternalLink, CheckCircle2, Instagram, Twitter as TwitterIcon,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import SEO from "@/components/SEO";
import { track, EVENT } from "@/lib/analytics";
import RegistryCertificatePremium from "@/pages/RegistryCertificatePremium";

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return iso; }
}

export default function CertificateDetail() {
  const { cert_number } = useParams();
  // Phase 7 — try the new HC/SC registry endpoint first. If the certificate
  // is a fully-published/verified HC or SC entry, render the premium page.
  // Otherwise fall through to the existing legacy verify path below.
  const [registryCert, setRegistryCert] = useState(null);   // null=loading, false=not-in-registry, obj=hit
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState("");
  const { collector } = useCollectorAuth();

  useEffect(() => {
    let cancelled = false;
    track(EVENT.CERTIFICATE_VIEWED, { cert_number });
    // Try registry first
    api.get(`/public/registry/certificates/${cert_number}`)
      .then((r) => { if (!cancelled) setRegistryCert(r.data); })
      .catch(() => {
        if (cancelled) return;
        setRegistryCert(false);
        // Legacy fallback for pre-registry certs (Phase 5 verify endpoint)
        api.get(`/public/certificates/${cert_number}/verify`)
          .then((r) => { if (!cancelled) setCert(r.data); })
          .catch((e) => { if (!cancelled) setErr(formatApiError(e.response?.data?.detail) || e.message); });
      });
    return () => { cancelled = true; };
  }, [cert_number]);

  const qrUrl = useMemo(
    () => (cert ? `${API}/public/certificates/${cert.cert_number}/qr.png` : null),
    [cert]
  );
  const ogUrl = useMemo(
    () => (cert ? `${API}/public/certificates/${cert.cert_number}/og.png` : null),
    [cert]
  );

  // Registry hit → premium HC/SC layout
  if (registryCert && typeof registryCert === "object") {
    return <RegistryCertificatePremium cert={registryCert} />;
  }

  if (err) {
    return (
      <div className="bg-[var(--cream)] min-h-[80vh]" data-testid="cert-not-found">
        <div className="container-gm py-24 text-center">
          <div className="kicker kicker-red">Not Found</div>
          <h1 className="h-display text-3xl md:text-5xl mt-4 text-[var(--ink)]">
            Certificate not on record.
          </h1>
          <p className="text-[var(--smoke)] mt-4">
            The number <span className="font-mono text-[var(--panini-red)]">{cert_number}</span> was not
            found in the certificates.
          </p>
          <Link to="/certificates" className="inline-block mt-8 text-[var(--panini-red)] underline underline-offset-4 font-bold">
            Return to certificates
          </Link>
        </div>
      </div>
    );
  }
  if (!cert) {
    return (
      <div className="bg-[var(--cream)] min-h-[80vh]">
        <div className="container-gm py-24 text-[var(--smoke)]">Loading…</div>
      </div>
    );
  }

  const publicUrl = cert.share?.public_url
    || `${window.location.origin}/certificates/${cert.cert_number}`;
  const isOwned = cert.ownership?.state === "owned";
  const ownerLink = cert.owner_profile;

  const shareCert = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          url: publicUrl,
          title: `${cert.cert_number} · Certificate of Completion`,
          text: `Verified Panini certificate ${cert.cert_number}`,
        });
      } else {
        await navigator.clipboard.writeText(publicUrl);
        toast.success("Certificate link copied.");
      }
    } catch { /* user cancelled */ }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Certificate link copied.");
    } catch { toast.error("Couldn't copy."); }
  };

  const socialShare = (kind) => {
    const encoded = encodeURIComponent(publicUrl);
    const text = encodeURIComponent(`Verified Panini certificate ${cert.cert_number}`);
    const urls = {
      x: `https://twitter.com/intent/tweet?url=${encoded}&text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}%20${encoded}`,
    };
    if (urls[kind]) window.open(urls[kind], "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bg-[var(--cream)] min-h-[80vh]" data-testid="cert-detail">
      <SEO
        title={`${cert.cert_number} · Verified Certificate · Gil Maakaroun`}
        description={`Verified ${cert.collection?.album_name || cert.album} certificate ${cert.cert_number}${isOwned ? ` — registered to ${cert.ownership?.public_collector_id || "a verified collector"}` : ""}.`}
        image={ogUrl}
        url={publicUrl}
      />

      {/* Print-only stylesheet for a clean paper layout */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          .no-print, .no-print * { display: none !important; }
          .print-only { display: block !important; }
          .cert-hero { box-shadow: none !important; border-radius: 0 !important; }
          a { color: #111 !important; text-decoration: none !important; }
        }
        .print-only { display: none; }
      `}</style>

      <div className="container-gm py-8 md:py-12 max-w-4xl">
        <Link
          to="/certificates"
          className="no-print inline-flex items-center gap-2 text-[var(--smoke)] hover:text-[var(--ink)] text-sm"
          data-testid="cert-back"
        >
          <ArrowLeft size={14} /> Back to certificates
        </Link>

        {/* HERO / VERIFICATION CARD */}
        <section
          data-testid="cert-hero"
          className="cert-hero mt-6 rounded-3xl border-2 border-[var(--ink)] bg-white shadow-[8px_8px_0_var(--ink)] overflow-hidden"
        >
          {/* Top verified band */}
          <div
            data-testid="cert-verified-band"
            className={
              "flex items-center justify-between px-6 md:px-10 py-4 " +
              (isOwned
                ? "bg-[#C5A059] text-white"
                : (cert.verified ? "bg-[var(--ink)] text-[var(--panini-yellow)]" : "bg-[var(--smoke)] text-white"))
            }
          >
            <div className="flex items-center gap-3">
              <ShieldCheck size={22} />
              <div>
                <div className="uppercase tracking-[0.24em] text-[11px] font-bold opacity-80">
                  Status
                </div>
                <div className="font-display text-lg md:text-xl">
                  {isOwned ? "Owned · Verified" : (cert.verified ? "Verified" : "Not verified")}
                </div>
              </div>
            </div>
            <div className="text-right hidden sm:block">
              <div className="uppercase tracking-[0.24em] text-[11px] font-bold opacity-80">Certificate</div>
              <div className="text-sm font-mono">collectorup.com</div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 md:px-10 py-8 md:py-12">
            <div className="kicker kicker-red">Certificate of Completion</div>

            {/* Certificate number — hero */}
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <h1
                data-testid="cert-number-hero"
                className="font-display text-4xl md:text-6xl leading-none text-[var(--ink)]"
                style={{ letterSpacing: "-0.01em" }}
              >
                {cert.cert_number}
              </h1>
              <button
                type="button"
                onClick={copyLink}
                className="no-print inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-[var(--smoke)] hover:text-[var(--panini-red)]"
                data-testid="cert-copy-link"
              >
                <Copy size={13} /> Copy link
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-10 mt-8">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                  Registered to
                </div>
                <div
                  data-testid="cert-collector-name"
                  className="font-display text-2xl md:text-3xl text-[var(--panini-red)] mt-1"
                >
                  {cert.display_name}
                </div>
                {isOwned && cert.ownership?.public_collector_id && (
                  <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--panini-yellow-soft)] font-mono text-xs font-bold text-[var(--ink)]"
                    data-testid="cert-owner-public-id">
                    {cert.ownership.public_collector_id}
                  </div>
                )}
                {ownerLink?.username && (
                  <div className="mt-3">
                    <Link
                      to={ownerLink.profile_url}
                      className="inline-flex items-center gap-1 text-sm font-bold text-[var(--panini-red)] hover:underline"
                      data-testid="cert-owner-profile-link"
                    >
                      View collector profile
                      <ExternalLink size={12} />
                    </Link>
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                  Album / Collection
                </div>
                <div className="text-[var(--ink)] mt-1 text-lg font-bold" data-testid="cert-album">
                  {cert.collection?.album_name || cert.album}
                </div>
                {cert.collection?.manufacturer && (
                  <div className="text-[var(--smoke)] text-xs mt-1">
                    {cert.collection.manufacturer}
                    {cert.collection.edition_year ? ` · ${cert.collection.edition_year}` : ""}
                  </div>
                )}

                {cert.completion_year && (
                  <div className="mt-4">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                      Completion year
                    </div>
                    <div className="text-[var(--ink)] mt-1" data-testid="cert-year">
                      {cert.completion_year}
                    </div>
                  </div>
                )}

                {cert.verification_date && (
                  <div className="mt-4">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                      Verified on
                    </div>
                    <div className="text-[var(--ink)] mt-1" data-testid="cert-verified-on">
                      {fmt(cert.verification_date)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Verification details row */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
              <VerifyStat
                testid="verify-stat-status"
                icon={<CheckCircle2 size={16} />}
                label="Status"
                value={cert.verified ? "Active" : (cert.status || "—")}
              />
              <VerifyStat
                testid="verify-stat-issued"
                icon={<CheckCircle2 size={16} />}
                label="Issued"
                value={fmt(cert.issued_at) || "—"}
              />
              <VerifyStat
                testid="verify-stat-ownership"
                icon={<CheckCircle2 size={16} />}
                label="Ownership"
                value={isOwned ? "Recorded" : "Unclaimed"}
              />
              <VerifyStat
                testid="verify-stat-registry"
                icon={<CheckCircle2 size={16} />}
                label="Certificate"
                value="collectorup.com"
              />
            </div>

            {cert.notes && (
              <div className="mt-8 rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-5" data-testid="cert-notes">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--ink)]">Notes</div>
                <p className="text-[var(--ink)] mt-2 whitespace-pre-wrap leading-relaxed">{cert.notes}</p>
              </div>
            )}
          </div>

          {/* QR + share block */}
          <div className="no-print border-t-2 border-[var(--ink)] bg-[var(--cream)] px-6 md:px-10 py-8">
            <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
              <div className="flex flex-col items-center gap-3">
                <div
                  data-testid="cert-qr-wrap"
                  className="w-40 h-40 rounded-2xl border-2 border-[var(--ink)] bg-white p-2 shadow-[4px_4px_0_var(--ink)] grid place-items-center"
                >
                  {qrUrl ? (
                    <img
                      src={qrUrl}
                      alt={`QR verify ${cert.cert_number}`}
                      className="w-full h-full object-contain"
                      data-testid="cert-qr-img"
                    />
                  ) : (
                    <QrCode size={40} />
                  )}
                </div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                  Scan to verify
                </div>
              </div>

              <div>
                <div className="kicker kicker-black">Share &amp; Verify</div>
                <h2 className="font-display text-xl md:text-2xl mt-1">
                  Anyone can verify this certificate.
                </h2>
                <p className="text-[var(--smoke)] text-sm mt-2 max-w-lg">
                  Scan the QR or open the link below. The verification page is public
                  and permanent — it will never change for this certificate number.
                </p>

                <div
                  data-testid="cert-public-url"
                  className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border-2 border-[var(--ink)] font-mono text-xs break-all"
                  style={{ maxWidth: "100%" }}
                >
                  {publicUrl}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={shareCert} className="btn-red text-sm" data-testid="cert-share-native">
                    <Share2 size={14} /> Share
                  </button>
                  <button onClick={() => window.print()} className="btn-ghost text-sm" data-testid="cert-print">
                    <Printer size={14} /> Print
                  </button>
                  <button
                    onClick={() => socialShare("x")}
                    className="btn-ghost text-sm"
                    data-testid="cert-share-x"
                    aria-label="Share on X"
                  >
                    <TwitterIcon size={14} /> X
                  </button>
                  <button
                    onClick={() => socialShare("facebook")}
                    className="btn-ghost text-sm"
                    data-testid="cert-share-facebook"
                    aria-label="Share on Facebook"
                  >
                    Facebook
                  </button>
                  <button
                    onClick={() => socialShare("whatsapp")}
                    className="btn-ghost text-sm"
                    data-testid="cert-share-whatsapp"
                    aria-label="Share on WhatsApp"
                  >
                    WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ownership banner (claim CTA) */}
        <OwnershipBanner cert={cert} collector={collector} />

        {/* Public socials (legacy field) */}
        {cert.socials && (cert.socials.instagram || cert.socials.tiktok || cert.socials.twitter) && (
          <div className="no-print mt-8 flex flex-wrap gap-3 justify-center" data-testid="cert-socials">
            {cert.socials.instagram && (
              <a
                href={`https://instagram.com/${cert.socials.instagram.replace(/^@/, "")}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white text-xs font-bold"
              >
                <Instagram size={13} /> {cert.socials.instagram}
              </a>
            )}
          </div>
        )}

        {/* Legal disclaimer */}
        <p className="text-[var(--smoke)] text-xs text-center mt-10 max-w-2xl mx-auto leading-relaxed" data-testid="cert-disclaimer">
          Gil Maakaroun&apos;s album-completion program is independently operated and is not affiliated
          with, endorsed by, or approved by Panini or FIFA.
        </p>
      </div>
    </div>
  );
}

function VerifyStat({ testid, icon, label, value }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-3"
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)] flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-sm font-bold text-[var(--ink)] mt-1 break-all">{value}</div>
    </div>
  );
}

function OwnershipBanner({ cert, collector }) {
  const o = cert.ownership || {};
  if (o.state === "owned") {
    return (
      <div
        className="no-print mt-8 rounded-2xl border-2 border-[#C5A059] bg-[#C5A059]/10 p-5 flex items-start gap-4"
        data-testid="ownership-owned"
      >
        <ShieldCheck className="text-[#C5A059] shrink-0 mt-1" size={22} />
        <div className="flex-1">
          <div className="uppercase tracking-widest text-xs font-bold text-[#C5A059]">
            Ownership recorded
          </div>
          <div className="text-[var(--ink)] mt-2 font-bold text-lg">
            Registered to{" "}
            {o.public_collector_id ? (
              <span className="font-mono text-[#C5A059]" data-testid="owner-public-id">
                {o.public_collector_id}
              </span>
            ) : (
              <span>a verified collector</span>
            )}
          </div>
          {o.claimed_at && (
            <div className="text-[var(--smoke)] text-xs mt-1">
              Ownership recorded {new Date(o.claimed_at).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    );
  }
  // unclaimed
  return (
    <div
      className="no-print mt-8 rounded-2xl border-2 border-[var(--ink)] bg-white p-5 flex items-start gap-4 shadow-[4px_4px_0_var(--ink)]"
      data-testid="ownership-unclaimed"
    >
      <ShieldAlert className="text-[var(--panini-red)] shrink-0 mt-1" size={22} />
      <div className="flex-1">
        <div className="uppercase tracking-widest text-xs font-bold text-[var(--ink)]">
          Unclaimed
        </div>
        <div className="text-[var(--ink)] mt-2 font-bold text-lg">
          This certificate has not yet been claimed by its collector.
        </div>
        <p className="text-[var(--smoke)] text-sm mt-2">
          If this belongs to you, sign in and use the one-time code from your
          certificate insert — or upload a legacy photo proof for review.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-3">
          {collector ? (
            <Link to={`/claim?cert=${cert.cert_number}`} className="btn-red text-sm" data-testid="ownership-cta-claim">
              Claim this certificate →
            </Link>
          ) : (
            <>
              <Link
                to={`/claim?cert=${cert.cert_number}`}
                className="btn-red text-sm w-full sm:w-auto justify-center"
                data-testid="ownership-cta-login"
              >
                Sign in and claim →
              </Link>
              <Link
                to={`/register?next=${encodeURIComponent(`/claim?cert=${cert.cert_number}`)}`}
                className="btn-black text-sm w-full sm:w-auto justify-center"
                data-testid="ownership-cta-register"
              >
                Create Collector account
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
